import { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { components } from 'react-select';
import InputField from '../../inputField';
import ActionButton from '../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import SelectField from '../../selectField';
import ConditionalTooltip from '../../conditionalTooltip';
import { RECRUITMENT_STEPS_MAP } from '../../../../constants/job';
import { primaryPalette } from '../../../../styles/theme';
import TextBoxWithTooltip from '../../textBoxWithTooltip';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'grid',
    gridTemplateColumns:
      'minMax(0, 34px) minMax(0, 72px) minMax(0, 248px) minMax(0, 32px)',
    gridColumnGap: 4,
    marginBottom: spacing(6),
  },
  field: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  errorMessage: {
    position: 'absolute',
    bottom: -20,
  },
  fieldWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: 'inherit',
  },
  deleteButton: {
    flexShrink: 0,
    marginTop: spacing(2),
  },
  select: {
    minWidth: 72,
  },
  input: {
    marginTop: spacing(2),
  },
  dragButton: {
    flexShrink: 0,
    marginTop: spacing(2),
    marginRight: 15,
  },
  deleteIcon: {
    justifyContent: 'center',
  },
  icon: {
    width: 24,
  },
  singleValue: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledIcon: {
    '& g, & path': {
      fill: primary.bluish5,
    },
    '& path': {
      stroke: primary.bluish5,
    },
  },
}));

const CustomMenuList = props => {
  return props.children;
};

const CustomOption = props => {
  const { icon: Icon } = RECRUITMENT_STEPS_MAP[props.data.id];
  return (
    <components.Option {...props}>
      <Icon />
      <TextBoxWithTooltip text={props.data.label} />
    </components.Option>
  );
};

const CustomSingleValue = ({ data, ...props }, isLastType) => {
  const classes = useStyles();
  const { icon: Icon } = RECRUITMENT_STEPS_MAP[data.id];

  return (
    <components.SingleValue className={classes.singleValue} {...props}>
      <Icon
        className={classNames(classes.icon, {
          [classes.disabledIcon]: isLastType,
        })}
      />
    </components.SingleValue>
  );
};

const RecruitmentStepsField = ({
  translations,
  id,
  field,
  steps = [],
  fieldTypes,
  options,
  error = null,
  onDelete,
  onChange,
  dragRef = () => {},
  dragProps = null,
}) => {
  const classes = useStyles();
  const [selectField, firstTextField] = fieldTypes;
  const isLastType =
    [RECRUITMENT_STEPS_MAP.SOURCED.id, RECRUITMENT_STEPS_MAP.HIRED.id].includes(
      field.type
    ) && steps.filter(step => step.type === field.type).length < 2;

  const getMessage = () => {
    const message = isLastType
      ? translations.defaultStepMessage
      : translations.hasCandidatesMessage;

    return field.has_review_note_or_message ? translations.hasData : message;
  };

  const handleChangeText = useCallback(
    (e, name) => {
      onChange({ ...field, [name]: e.target.value }, id);
    },
    [id, field, onChange]
  );

  const handleChangeSelect = useCallback(
    (value, name) => {
      onChange(
        { ...field, [name]: value, title: RECRUITMENT_STEPS_MAP[value].title },
        id
      );
    },
    [id, field, onChange]
  );

  return (
    <div
      ref={dragRef}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
      className={classes.root}
    >
      <div className={classes.field}>
        <ConditionalTooltip
          className={classes.dragButton}
          message={translations.draggableTooltip}
          addTooltip
        >
          <ActionButton
            type={ACTION_BUTTON_TYPES.DRAG}
            {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
          />
        </ConditionalTooltip>
      </div>
      <div className={classNames(classes.field)}>
        <div className={classes.fieldWrapper}>
          <ConditionalTooltip
            message={translations.defaultStepSelectMessage}
            addTooltip={isLastType}
          >
            <SelectField
              className={classes.select}
              errorClass={classes.errorMessage}
              value={field?.[selectField.name]}
              parser={{ value: 'id', label: 'title' }}
              hasError={!!error?.[selectField.name]}
              errorMessage={
                translations.validationMessages?.[error?.[selectField.name]]
              }
              components={{
                SingleValue: data => CustomSingleValue(data, isLastType),
                IndicatorSeparator: () => null,
                MenuList: CustomMenuList,
                Option: CustomOption,
              }}
              styles={{
                option: (base, state) => ({
                  ...base,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor:
                    state.isFocused || state.isSelected
                      ? primaryPalette.bluish8
                      : primaryPalette.white,
                }),
                control: base => ({
                  ...base,
                  height: 40,
                }),
                singleValue: base => ({
                  ...base,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }),
                menu: base => ({
                  ...base,
                  minWidth: '150px',
                  overflow: 'hidden',
                }),
                valueContainer: provided => ({
                  ...provided,
                  paddingRight: 0,
                }),
              }}
              options={options}
              onChange={value => handleChangeSelect(value, selectField.name)}
              isDisabled={isLastType}
              isSearchDisabled
            />
          </ConditionalTooltip>
        </div>
      </div>
      <div className={classNames(classes.field)}>
        <div className={classes.fieldWrapper}>
          <InputField
            className={classes.input}
            customErrorClass={classes.errorMessage}
            id={firstTextField.name}
            name={firstTextField.name}
            type={firstTextField.type}
            placeholder={
              translations[firstTextField.translationKey].placeholder
            }
            required={firstTextField.required}
            value={field?.[firstTextField.name] || ''}
            error={!!error?.[firstTextField.name]}
            errorMessage={
              translations.validationMessages?.[error?.[firstTextField.name]]
            }
            onChange={e => handleChangeText(e, firstTextField.name)}
            fullWidth
          />
        </div>
      </div>
      <div className={classNames(classes.field)}>
        <div className={classNames(classes.fieldWrapper, classes.deleteIcon)}>
          <ActionButton
            className={classes.deleteButton}
            type={ACTION_BUTTON_TYPES.DELETE_FILL}
            onClickHandler={onDelete}
            isDisabled={
              field.hasCandidates ||
              isLastType ||
              field.has_review_note_or_message
            }
            disabledTooltipText={getMessage()}
          />
        </div>
      </div>
    </div>
  );
};

RecruitmentStepsField.propTypes = {
  translations: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  field: PropTypes.object.isRequired,
  fieldTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  steps: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.object,
  onDelete: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  dragProps: PropTypes.object,
  dragRef: PropTypes.func,
};

export default memo(RecruitmentStepsField);
